import { Box, ListItemButton, ListItemIcon, ListItemText, Stack, Theme } from '@mui/material';
import { FC, useState, useEffect, MouseEvent } from 'react';
import { SidebarItem } from './helpers';
import { createUseStyles } from 'react-jss';
import classNames from 'classnames';
import { openNewTab, pathIsActive } from '@/lib/url-helpers';
import Icon from '../icon';
import Tooltip from '../tooltip';
import CollapseBox from '../collapse-box';
import { IconColor } from '../icon/helpers';

interface Props {
  item: SidebarItem;
  onNavigate: (href?: string) => void;
  isDrawerOpen: boolean;
  subItems?: SidebarItem[];
  onOpen?: (open: boolean) => void;
  color?: IconColor;
}

const OPEN_MS = 200;

const useStyles = createUseStyles<
  string,
  { drawerOpen: boolean; subItemsOpen: boolean; color?: IconColor }
>((theme: Theme) => ({
  menuButton: {
    color: theme.palette.navigation.text,
    display: 'flex',
    alignItems: 'center',
    padding: ({ drawerOpen }) => (drawerOpen ? '6px 8px 6px 16px' : '8px'),
    gap: ({ drawerOpen }) => (drawerOpen ? 8 : 0),
    width: ({ drawerOpen }) => (drawerOpen ? 'inherit' : 36),
    justifyContent: 'center',
    ' &:hover ': {
      color: theme.palette.navigation.hoverText,
      backgroundColor: `${theme.palette.navigation.hover} !important`,
      borderRadius: 4,
    },
    '&:active': {
      color: `${theme.palette.navigation.text} !important`,
    },
    '&:not(.isParent).Mui-selected': {
      color: theme.palette.navigation.selectedText,
      backgroundColor: theme.palette.navigation.selected,
      borderRadius: 4,
    },
    '&:not(.isParent).Mui-selected .MuiSvgIcon-root': {
      color: theme.palette.navigation.selectedText,
    },
    '&:hover .MuiSvgIcon-root': {
      color: theme.palette.navigation.hoverText,
    },
    '& .MuiListItemIcon-root': {
      minWidth: 20,
      color: theme.palette.navigation.text,
    },
    '& .MuiListItemText-root': {
      flexGrow: 0,
    },
  },
  expandButton: {
    transform: ({ subItemsOpen }) => (subItemsOpen ? '' : 'rotate(180deg)'),
    transition: `transform ${OPEN_MS}ms`,
  },
  subItems: {
    borderLeft: `1px solid ${theme.palette.navigation.text}`,
    marginLeft: 30,
    '& $menuButton': {
      padding: ({ drawerOpen }) => (drawerOpen ? '6px 8px' : '8px'),
    },
  },
  menuItemText: {
    color: ({ color }) => color || 'inherit',
    fontSize: theme.typography.body2.fontSize,
  },
}));

const MenuItem: FC<Props> = ({ item, onNavigate, isDrawerOpen, subItems = [], onOpen, color }) => {
  const { link, icon, comingSoon, label } = item;
  const isParent = !!subItems.length;
  const subItemsActive = subItems.some(
    ({ link: subItemLink }) => subItemLink && pathIsActive(subItemLink, true)
  );
  const [subItemsOpen, setSubItemsOpen] = useState(subItemsActive);

  useEffect(() => {
    if (subItemsActive) {
      setSubItemsOpen(true);
    }
  }, [subItemsActive]);

  const styles = useStyles({ drawerOpen: isDrawerOpen, subItemsOpen, color });
  const compClass = classNames(styles.menuButton, { isParent });
  const active = link ? pathIsActive(link, true) : false;
  const hasIcon = !!icon;

  const handleClick = (event: MouseEvent) => {
    if (comingSoon) {
      return;
    }

    if (isParent) {
      const updatedOpen = !subItemsOpen;
      setSubItemsOpen(updatedOpen);

      if (onOpen) {
        // due to the transformation, the sub-item menu isn't open until after
        // OPEN_MS, so wait for that to fire the onOpen callback
        setTimeout(() => {
          onOpen?.(updatedOpen);
        }, OPEN_MS + 10);
      }

      return;
    }

    if (link) {
      // ctrl+click opens new tab
      if (event.ctrlKey || event.metaKey) {
        openNewTab(link);
        return;
      }

      onNavigate(link);
    } else {
      onNavigate();
    }
  };

  const BaseMenuItemEl = (
    <Box key={label} mx={isDrawerOpen ? 0.5 : 0.25} my={isDrawerOpen ? 0 : 0.25}>
      <ListItemButton
        className={compClass}
        onClick={handleClick}
        selected={active}
        aria-selected={active}
        disableRipple={!link}
      >
        {hasIcon && (
          <ListItemIcon>
            <Icon name={icon} color={color} size="small" />
          </ListItemIcon>
        )}
        {isDrawerOpen && (
          <ListItemText primary={label} className={styles.menuItemText} disableTypography />
        )}
        <Stack flexGrow={1} direction="row" alignItems="center">
          {isDrawerOpen && comingSoon && (
            <Icon name="lock-clock" size="small" className={styles.icon} />
          )}
          {isDrawerOpen && !comingSoon && isParent && (
            <Icon name="expand" size="small" className={styles.expandButton} />
          )}
        </Stack>
      </ListItemButton>
    </Box>
  );

  const WrappedMenuItemEl = (
    <Tooltip placement="right" title={label}>
      {BaseMenuItemEl}
    </Tooltip>
  );

  const MenuItemEl = isDrawerOpen ? BaseMenuItemEl : WrappedMenuItemEl;

  if (comingSoon) {
    return (
      <Tooltip placement="right" title="This feature will be available soon">
        {MenuItemEl}
      </Tooltip>
    );
  }

  if (!isParent) {
    return MenuItemEl;
  }

  return isDrawerOpen ? (
    <Stack>
      {MenuItemEl}
      <CollapseBox className={styles.subItems} open={subItemsOpen}>
        {subItems.map((subItem) => (
          <MenuItem
            item={subItem}
            onNavigate={onNavigate}
            isDrawerOpen={isDrawerOpen}
            key={subItem.key}
          />
        ))}
      </CollapseBox>
    </Stack>
  ) : (
    <>
      {subItems.map((subItem) => (
        <MenuItem
          item={subItem}
          onNavigate={onNavigate}
          isDrawerOpen={isDrawerOpen}
          key={subItem.key}
        />
      ))}
    </>
  );
};

export default MenuItem;
